export interface ServiceAreaStat {
  value: string;
  label: string;
}

export interface ServiceArea {
  slug: string;
  href: string;
  eyebrow: string;
  title: string;
  intro: string;
  description: string;
  highlights: string[];
  stats: ServiceAreaStat[];
  image: string;
  imageAlt: string;
  ctaLabel: string;
  tone: "spine" | "ortho";
}

/**
 * The two primary specialist service areas of Ortho Spine Care. Both live under
 * the Services section and share this single source of truth so the Services
 * landing page and internal links stay consistent.
 */
export const serviceAreas: ServiceArea[] = [
  {
    slug: "spine-care",
    href: "/services/spine-care",
    eyebrow: "Spine Care",
    title: "Advanced care for your spine",
    intro: "Focused, senior-level care for the neck and back.",
    description:
      "From accurate diagnosis to minimally invasive and complex revision surgery, our spine care covers the full pathway for neck and back conditions — always considering non-surgical options first.",
    highlights: [
      "Cervical & lumbar disc prolapse",
      "Persistent back pain & neck pain",
      "Sciatica & nerve-related pain",
      "Spinal stenosis & spine trauma",
      "Minimally invasive spine surgery",
      "Complex & revision spine surgery",
    ],
    stats: [
      { value: "10+", label: "Years of surgical experience" },
      { value: "2000+", label: "Spine surgeries performed" },
      { value: "MIS", label: "Minimally invasive & revision care" },
    ],
    image:
      "https://readdy.ai/api/search-image?query=Detailed%20three%20dimensional%20anatomical%20render%20of%20the%20human%20lumbar%20spine%20and%20vertebrae%20on%20a%20clean%20light%20studio%20surface%2C%20soft%20cyan%20teal%20and%20royal%20blue%20lighting%2C%20premium%20medical%20education%20visual%2C%20no%20people&width=1200&height=900&seq=ospc-services-spine-2026&orientation=landscape",
    imageAlt: "Three dimensional anatomical render of the human spine illustrating spine care",
    ctaLabel: "Explore Spine Care",
    tone: "spine",
  },
  {
    slug: "orthopaedic-care",
    href: "/services/orthopaedic-care",
    eyebrow: "Orthopaedic Care",
    title: "Comprehensive care for bones & joints",
    intro: "Specialist care for the bones, joints, muscles and tendons.",
    description:
      "Whether the problem is arthritis, a sports injury, a fracture or a bone infection, we reach an accurate diagnosis first and reserve surgery — arthroscopy and joint replacement — for when it is genuinely needed.",
    highlights: [
      "Joint replacement for arthritis",
      "Arthroscopy for ligament injuries",
      "Complex trauma & bone fractures",
      "Sports injuries & recovery care",
      "Bone infections & bone deformities",
      "Tendon & soft-tissue treatment",
    ],
    stats: [
      { value: "Joint", label: "Replacement surgery" },
      { value: "Arthroscopy", label: "Keyhole joint surgery" },
      { value: "Trauma", label: "Complex fracture care" },
    ],
    image:
      "https://readdy.ai/api/search-image?query=Detailed%20three%20dimensional%20anatomical%20renders%20of%20a%20human%20knee%20and%20hip%20joint%20with%20bone%20and%20cartilage%20highlighted%20on%20a%20clean%20light%20studio%20surface%2C%20soft%20royal%20blue%20and%20violet%20lighting%2C%20premium%20medical%20education%20visual%2C%20no%20people&width=1200&height=900&seq=ospc-services-ortho-2026&orientation=landscape",
    imageAlt: "Three dimensional anatomical renders of the knee and hip joints illustrating orthopaedic care",
    ctaLabel: "Explore Orthopaedic Care",
    tone: "ortho",
  },
];